import { 
  SalesLot, 
  CreateLotRequest, 
  MarketplaceSearchFilters, 
  MarketplaceSearchResult,
  LotStatus,
  LotItem
} from '../types/marketplaceTypes';

// Mock storage - in a real app, this would be an API/database
let salesLotsStorage: SalesLot[] = [
  // Sample data for demonstration
  {
    id: 'lot-001',
    organizationId: 'org-1',
    organizationName: 'Fazenda São João',
    title: 'Lote de Gado Nelore Premium',
    itemType: 'animal',
    items: [
      {
        id: 'item-001',
        identifier: '#123',
        description: 'Touro Nelore',
        weight: 580,
        age: 24,
        breed: 'Nelore',
        condition: 'Excelente'
      },
      {
        id: 'item-002',
        identifier: '#124',
        description: 'Touro Nelore',
        weight: 620,
        age: 30,
        breed: 'Nelore',
        condition: 'Excelente'
      }
    ],
    pricePerItem: 2000,
    currency: 'BRL',
    totalValue: 4000,
    status: 'for_sale',
    description: 'Animais de alta qualidade genética, vacinados e com documentação completa.',
    location: 'São Paulo, SP',
    contactInfo: {
      phone: '(11) 99999-9999',
      email: 'vendas@fazendajoao.com.br',
      preferredContact: 'phone'
    },
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(), // 2 days ago
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    viewCount: 15,
    isUrgent: false,
    tags: ['nelore', 'reprodutor', 'premium', 'vacinado']
  }
];

let nextLotId = 2;
let nextItemId = 3;

class MarketplaceService {
  // Create a new sales lot
  async createSalesLot(request: CreateLotRequest): Promise<SalesLot> {
    const lotId = `lot-${String(nextLotId++).padStart(3, '0')}`;
    
    // Generate items with IDs
    const items: LotItem[] = request.items.map(item => ({
      ...item,
      id: `item-${String(nextItemId++).padStart(3, '0')}`
    }));

    const totalValue = items.length * request.pricePerItem;
    
    // Get organization name (in a real app, this would be fetched from a service)
    const organizationName = this.getOrganizationName(request.organizationId);

    const newLot: SalesLot = {
      id: lotId,
      organizationId: request.organizationId,
      organizationName,
      title: request.title || this.generateDefaultTitle(request.itemType, items.length),
      itemType: request.itemType,
      items,
      pricePerItem: request.pricePerItem,
      currency: request.currency || 'BRL',
      totalValue,
      status: 'for_sale',
      description: request.description,
      location: request.location,
      contactInfo: request.contactInfo,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      expiresAt: request.expiresAt,
      viewCount: 0,
      isUrgent: request.isUrgent || false,
      tags: request.tags || []
    };

    salesLotsStorage.unshift(newLot); // Add to beginning for newest first
    return newLot;
  }

  // Get a specific lot by ID
  async getLotById(lotId: string): Promise<SalesLot | null> {
    const lot = salesLotsStorage.find(lot => lot.id === lotId);
    
    if (lot) {
      // Increment view count
      lot.viewCount++;
      lot.updatedAt = new Date().toISOString();
    }
    
    return lot || null;
  }

  // Search for lots with filters
  async searchLots(filters: MarketplaceSearchFilters = {}): Promise<MarketplaceSearchResult> {
    let filteredLots = [...salesLotsStorage];

    // Apply filters
    if (filters.itemType) {
      filteredLots = filteredLots.filter(lot => lot.itemType === filters.itemType);
    }

    if (filters.organizationId) {
      filteredLots = filteredLots.filter(lot => lot.organizationId === filters.organizationId);
    }

    if (filters.status) {
      filteredLots = filteredLots.filter(lot => lot.status === filters.status);
    } else {
      // Default to only show available lots
      filteredLots = filteredLots.filter(lot => lot.status === 'for_sale');
    }

    if (filters.minPrice !== undefined) {
      filteredLots = filteredLots.filter(lot => lot.pricePerItem >= filters.minPrice!);
    }

    if (filters.maxPrice !== undefined) {
      filteredLots = filteredLots.filter(lot => lot.pricePerItem <= filters.maxPrice!);
    }

    if (filters.location) {
      filteredLots = filteredLots.filter(lot => 
        lot.location?.toLowerCase().includes(filters.location!.toLowerCase())
      );
    }

    if (filters.tags && filters.tags.length > 0) {
      filteredLots = filteredLots.filter(lot =>
        filters.tags!.some(tag => 
          lot.tags.some(lotTag => 
            lotTag.toLowerCase().includes(tag.toLowerCase())
          )
        )
      );
    }

    // Sort by creation date (newest first)
    filteredLots.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return {
      lots: filteredLots,
      totalCount: filteredLots.length,
      filters
    };
  }

  // Get lots by organization
  async getLotsByOrganization(organizationId: string): Promise<SalesLot[]> {
    return salesLotsStorage.filter(lot => lot.organizationId === organizationId);
  }

  // Update lot status
  async updateLotStatus(lotId: string, status: LotStatus): Promise<SalesLot | null> {
    const lotIndex = salesLotsStorage.findIndex(lot => lot.id === lotId);
    
    if (lotIndex === -1) {
      return null;
    }

    salesLotsStorage[lotIndex] = {
      ...salesLotsStorage[lotIndex],
      status,
      updatedAt: new Date().toISOString()
    };

    return salesLotsStorage[lotIndex];
  }

  // Delete a lot
  async deleteLot(lotId: string): Promise<boolean> {
    const initialLength = salesLotsStorage.length;
    salesLotsStorage = salesLotsStorage.filter(lot => lot.id !== lotId);
    return salesLotsStorage.length < initialLength;
  }

  // Get recent lots
  async getRecentLots(limit: number = 10): Promise<SalesLot[]> {
    return salesLotsStorage
      .filter(lot => lot.status === 'for_sale')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, limit);
  }

  // Get urgent lots
  async getUrgentLots(): Promise<SalesLot[]> {
    return salesLotsStorage.filter(lot => lot.isUrgent && lot.status === 'for_sale');
  }

  // Helper methods
  private getOrganizationName(organizationId: string): string {
    // In a real app, this would fetch from an organization service
    const organizationNames: Record<string, string> = {
      'org-1': 'Fazenda São João',
      'org-2': 'Frigorífico Central',
      'org-3': 'Laticínios Vale Verde',
      'org-4': 'Cooperativa Agro Sul',
      'muu-general': 'Assistente MUU'
    };
    
    return organizationNames[organizationId] || 'Organização Desconhecida';
  }

  private generateDefaultTitle(itemType: string, itemCount: number): string {
    const typeLabels: Record<string, string> = {
      animal: 'Animais',
      fertilizer: 'Fertilizantes',
      nutrition: 'Nutrição Animal',
      equipment: 'Equipamentos',
      feed: 'Ração',
      medicine: 'Medicamentos',
      other: 'Produtos'
    };

    const typeLabel = typeLabels[itemType] || 'Produtos';
    return `Lote de ${typeLabel} (${itemCount} ${itemCount === 1 ? 'item' : 'itens'})`;
  }

  // Get marketplace statistics
  async getMarketplaceStats(): Promise<{
    totalLots: number;
    activeLots: number;
    totalValue: number;
    averagePrice: number;
    topCategories: Array<{ type: string; count: number }>;
  }> {
    const activeLots = salesLotsStorage.filter(lot => lot.status === 'for_sale');
    const totalValue = activeLots.reduce((sum, lot) => sum + lot.totalValue, 0);
    const averagePrice = activeLots.length > 0 ? totalValue / activeLots.reduce((sum, lot) => sum + lot.items.length, 0) : 0;

    // Count by category
    const categoryCount: Record<string, number> = {};
    activeLots.forEach(lot => {
      categoryCount[lot.itemType] = (categoryCount[lot.itemType] || 0) + 1;
    });

    const topCategories = Object.entries(categoryCount)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalLots: salesLotsStorage.length,
      activeLots: activeLots.length,
      totalValue,
      averagePrice,
      topCategories
    };
  }
}

// Export singleton instance
export const marketplaceService = new MarketplaceService(); 